export interface LlmRuntimeProfile {
  id: string;
  name: string;
  provider: string;
  modelId: string;
  endpointUrl?: string;
  isDefault: boolean;
}

export interface LlmRoleBinding {
  role: string;
  profileId: string;
}

export interface LlmBindingValidationIssue {
  role: string;
  profileId?: string;
}

export interface LlmRoleBindingValidation {
  missingRoles: string[];
  duplicateRoles: string[];
  unknownProfileBindings: LlmBindingValidationIssue[];
  isValid: boolean;
}

export interface WorkspaceLlmResolutionIssue {
  role: string;
  source: 'org-default' | 'workspace-override';
  profileId?: string;
}

export interface WorkspaceLlmResolutionValidation {
  unresolvedRoles: WorkspaceLlmResolutionIssue[];
  isValid: boolean;
}

export interface VoiceRuntimeProfile {
  id: string;
  provider: string;
  mode: string;
  endpointUrl?: string;
  deployment?: string;
  apiVersion?: string;
  /** Transcription model for input_audio_transcription (whisper-1 / gpt-4o-transcribe).
   *  Distinct from `deployment` (the realtime conversation model). */
  transcriptionModel?: string;
  apiKeySettingKey?: string;
  collectionId?: string;
  voiceIds?: string[];
  serverLocation?: string;
  displayName?: string;
  description?: string;
  latencyClass?: string;
  costClass?: string;
  isDefault: boolean;
}

export interface EmbeddingRuntimeProfile {
  id: string;
  name: string;
  provider: string;
  modelId: string;
  dimensions: number;
  endpointUrl?: string;
  isDefault: boolean;
}

export const LLM_RUNTIME_ROLES = [
  'chat.default',
  'retrieval.answer',
  'retrieval.grounding',
  'help.assistant',
  'voice.text',
  'ingest.extraction',
  'ingest.classification',
  'ingest.triage',
  'vision.analysis',
  'vision.chart',
] as const;

export function parseLlmProfiles(json: string | undefined): LlmRuntimeProfile[] {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    return Array.isArray(parsed) ? (parsed as LlmRuntimeProfile[]) : [];
  } catch {
    return [];
  }
}

export function parseLlmRoleBindings(json: string | undefined): LlmRoleBinding[] {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((entry: unknown): LlmRoleBinding | null => {
        if (!entry || typeof entry !== 'object') return null;
        const record = entry as Record<string, unknown>;
        const role = record.role ?? record.Role;
        const profileId = record.profileId ?? record.ProfileId;
        if (typeof role !== 'string' || typeof profileId !== 'string') return null;
        if (!role || !profileId) return null;
        return { role, profileId };
      })
      .filter((binding): binding is LlmRoleBinding => binding !== null);
  } catch {
    return [];
  }
}

export function parseWorkspaceLlmRoleOverrides(settingsJson: Record<string, unknown> | undefined): LlmRoleBinding[] {
  if (!settingsJson) return [];
  const raw = settingsJson.llmRoleOverrides;
  return Array.isArray(raw) ? (raw as LlmRoleBinding[]) : [];
}

export function validateLlmRoleBindings(
  profiles: LlmRuntimeProfile[],
  bindings: LlmRoleBinding[],
): LlmRoleBindingValidation {
  const profileIds = new Set(profiles.map((profile) => profile.id));
  const seenRoles = new Set<string>();
  const duplicateRoles = new Set<string>();

  bindings.forEach((binding) => {
    if (seenRoles.has(binding.role)) {
      duplicateRoles.add(binding.role);
      return;
    }

    seenRoles.add(binding.role);
  });

  const missingRoles = LLM_RUNTIME_ROLES.filter((role) => !seenRoles.has(role));
  const unknownProfileBindings = bindings.filter((binding) => !profileIds.has(binding.profileId));

  return {
    missingRoles,
    duplicateRoles: Array.from(duplicateRoles),
    unknownProfileBindings,
    isValid: missingRoles.length === 0 && duplicateRoles.size === 0 && unknownProfileBindings.length === 0,
  };
}

export function validateWorkspaceLlmResolution(
  profiles: LlmRuntimeProfile[],
  orgBindings: LlmRoleBinding[],
  overrides: LlmRoleBinding[],
): WorkspaceLlmResolutionValidation {
  const profileIds = new Set(profiles.map((profile) => profile.id));
  const orgBindingByRole = new Map(orgBindings.map((binding) => [binding.role, binding.profileId]));
  const overrideByRole = new Map(overrides.map((binding) => [binding.role, binding.profileId]));
  const unresolvedRoles: WorkspaceLlmResolutionIssue[] = [];

  LLM_RUNTIME_ROLES.forEach((role) => {
    const overrideProfileId = overrideByRole.get(role);
    if (overrideProfileId) {
      if (!profileIds.has(overrideProfileId)) {
        unresolvedRoles.push({
          role,
          source: 'workspace-override',
          profileId: overrideProfileId,
        });
      }
      return;
    }

    const orgProfileId = orgBindingByRole.get(role);
    if (!orgProfileId) {
      unresolvedRoles.push({ role, source: 'org-default' });
      return;
    }

    if (!profileIds.has(orgProfileId)) {
      unresolvedRoles.push({
        role,
        source: 'org-default',
        profileId: orgProfileId,
      });
    }
  });

  return {
    unresolvedRoles,
    isValid: unresolvedRoles.length === 0,
  };
}

// Curated Azure transcription models for input_audio_transcription.model.
// Keep in sync with services/voice_runtime.py AZURE_TRANSCRIPTION_MODELS.
export const DEFAULT_TRANSCRIPTION_MODEL = 'whisper-1';
export const TRANSCRIPTION_MODELS = ['whisper-1', 'gpt-4o-transcribe', 'gpt-4o-mini-transcribe'] as const;

/**
 * Effective transcription model for a voice profile's input_audio_transcription.
 * Mirrors the Python resolver (services/voice_runtime.resolve_transcription_model):
 * a valid configured model is passed through, anything else falls back to whisper-1.
 */
export function getEffectiveTranscriptionModel(profile: VoiceRuntimeProfile): string {
  const configured = (profile.transcriptionModel ?? '').trim();
  return (TRANSCRIPTION_MODELS as readonly string[]).includes(configured)
    ? configured
    : DEFAULT_TRANSCRIPTION_MODEL;
}

export function parseVoiceProfiles(json: string | undefined): VoiceRuntimeProfile[] {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    return Array.isArray(parsed) ? (parsed as VoiceRuntimeProfile[]) : [];
  } catch {
    return [];
  }
}

export function parseEmbeddingProfiles(json: string | undefined): EmbeddingRuntimeProfile[] {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    return Array.isArray(parsed) ? (parsed as EmbeddingRuntimeProfile[]) : [];
  } catch {
    return [];
  }
}

export function parseWorkspaceVoiceProfileId(settingsJson: Record<string, unknown> | undefined): string | undefined {
  if (!settingsJson) return undefined;
  const raw = settingsJson.voiceProfileId;
  if (typeof raw !== 'string') {
    return undefined;
  }

  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

export function getDefaultVoiceProfile(
  profiles: VoiceRuntimeProfile[],
  defaultProfileId?: string,
): VoiceRuntimeProfile | undefined {
  if (defaultProfileId) {
    const configuredDefault = profiles.find((profile) => profile.id === defaultProfileId);
    if (configuredDefault) {
      return configuredDefault;
    }
  }

  return profiles.find((profile) => profile.isDefault) ?? profiles[0];
}

export function getVoiceProviderFromProfile(
  profile: VoiceRuntimeProfile | undefined,
): 'azure-realtime' | 'elevenlabs' | undefined {
  if (profile?.provider === 'azure-realtime' || profile?.provider === 'elevenlabs') {
    return profile.provider;
  }

  return undefined;
}

export function parseWorkspaceEmbeddingProfileId(settingsJson: Record<string, unknown> | undefined): string | undefined {
  if (!settingsJson) return undefined;
  const raw = settingsJson.embeddingProfileId;
  if (typeof raw !== 'string') {
    return undefined;
  }

  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}
